import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * What's Next — where a loan stands, what blocks it, and whose turn it is.
 *
 * One read-only query. The server derives everything (stage, needs, hand-offs,
 * the milestone house) from data the other tabs own, so the tab is stale as
 * soon as someone works another tab; callers pass `refetchOnMountOrArgChange`
 * so returning to What's Next always shows the file as it is now.
 *
 * The endpoint answers `{status, message, data}`; consumers get `data`.
 */
export const whatsNextAPI = createApi({
  reducerPath: 'whatsNext',
  tagTypes: ['WhatsNext'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    getWhatsNext: builder.query({
      query: ({ loanId }) => ({
        url: `/b2b/loan/applications/${loanId}/whats-next`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, { loanId }) => [{ type: 'WhatsNext', id: loanId }],
    }),
  }),
});

/**
 * Refetch What's Next after any of the user's own writes, on any tab.
 *
 * This slice is separate from the ones the tabs mutate through, so their
 * invalidations never reach it. Rather than list every mutation that could
 * complete a milestone (profile, documents, credit, AUS, pricing …), every
 * fulfilled mutation on every other slice invalidates the tag.
 *
 * Cheap by construction: invalidation only refetches a subscribed entry (the
 * celebration provider on the loan-file shell, or the tab), RTK Query never
 * runs two requests for one entry at once, and a burst of saves collapses
 * into one refetch after REFRESH_DEBOUNCE_MS of quiet.
 */
const REFRESH_DEBOUNCE_MS = 800;
let refreshTimer = null;

export const whatsNextRefreshMiddleware = (storeApi) => (next) => (action) => {
  const result = next(action);
  const type = typeof action?.type === 'string' ? action.type : '';
  if (type.endsWith('/executeMutation/fulfilled') && !type.startsWith(`${whatsNextAPI.reducerPath}/`)) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      storeApi.dispatch(whatsNextAPI.util.invalidateTags(['WhatsNext']));
    }, REFRESH_DEBOUNCE_MS);
  }
  return result;
};

export const { useGetWhatsNextQuery } = whatsNextAPI;
